import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { BookOpen, Users, Star, Clock, Plus, Edit, Eye, RefreshCw, AlertCircle, Globe, EyeOff, Layout, ArrowUp, ArrowDown, Lightbulb } from 'lucide-react';

const statusLabel = { published: 'Publié', draft: 'Brouillon', archived: 'Archivé' };
const statusClass  = {
  published: 'bg-green-100 text-green-800',
  draft:     'bg-yellow-100 text-yellow-800',
  archived:  'bg-gray-100 text-gray-600',
};

const DIFFICULTY_LABELS = { beginner: 'Débutant', intermediate: 'Intermédiaire', advanced: 'Avancé' };
const DIFFICULTY_UNMAP  = { 'Débutant': 'beginner', 'Intermédiaire': 'intermediate', 'Avancé': 'advanced' };

const timeAgo = (iso) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60)  return `Il y a ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `Il y a ${h}h`;
  return `Il y a ${Math.floor(h / 24)} jour(s)`;
};

const Instructor = () => {
  const navigate = useNavigate();

  const [stats,    setStats]    = useState(null);
  const [courses,  setCourses]  = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsRes, coursesRes] = await Promise.all([
        axios.get('/api/instructor/stats'),
        axios.get('/api/instructor/courses'),
      ]);
      setStats(statsRes.data.stats);
      setActivity(statsRes.data.recentActivity || []);
      setCourses(coursesRes.data.courses || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const togglePublish = async (courseId, currentStatus) => {
    const newStatus = currentStatus === 'published' ? 'draft' : 'published';
    try {
      await axios.patch(`/api/courses/${courseId}/publish`, { status: newStatus });
      setCourses(prev => prev.map(c => c.id === courseId ? { ...c, status: newStatus } : c));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors de la publication');
    }
  };

  const changeLevel = async (courseId, newLabel) => {
    const difficulty = DIFFICULTY_UNMAP[newLabel];
    if (!difficulty) return;
    try {
      await axios.put(`/api/courses/${courseId}`, { difficulty });
      setCourses(prev => prev.map(c => c.id === courseId ? { ...c, difficulty } : c));
    } catch (err) {
      alert(err.response?.data?.error || 'Erreur lors du changement de niveau');
    }
  };

  const moveCourse = async (index, direction) => {
    const newCourses = [...courses];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newCourses.length) return;
    [newCourses[index], newCourses[swapIndex]] = [newCourses[swapIndex], newCourses[index]];
    setCourses(newCourses);
    try {
      await axios.patch('/api/instructor/courses/reorder', { courseIds: newCourses.map(c => c.id) });
    } catch (err) {
      setCourses(courses); // revert on error
      alert(err.response?.data?.error || 'Erreur lors du réordonnancement');
    }
  };

  const statCards = stats ? [
    { icon: BookOpen, value: stats.totalCourses,      label: 'Cours créés',    color: 'bg-blue-500' },
    { icon: Users,    value: stats.totalStudents,      label: 'Étudiants',      color: 'bg-green-500' },
    { icon: Clock,    value: `${stats.totalHours}h`,  label: 'Contenu total',  color: 'bg-purple-500' },
  ] : [];

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-text mb-2">Tableau de bord instructeur</h1>
            <p className="text-gray-600">Gérez vos cours et suivez vos performances</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={load}
              disabled={loading}
              className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              title="Actualiser"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => navigate('/instructor/create-course')}
              className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primary/90 transition-colors flex items-center gap-2"
            >
              <Plus className="w-5 h-5" />
              Créer un cours
            </button>
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 mb-6 bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-700 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Stats */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-white rounded-lg shadow-sm p-6 animate-pulse">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-200 rounded-lg" />
                  <div className="space-y-2">
                    <div className="h-6 w-16 bg-gray-200 rounded" />
                    <div className="h-4 w-24 bg-gray-100 rounded" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statCards.map(stat => (
              <div key={stat.label} className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center">
                  <div className={`w-12 h-12 ${stat.color} rounded-lg flex items-center justify-center mr-4`}>
                    <stat.icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-text">{stat.value}</div>
                    <div className="text-gray-600 text-sm">{stat.label}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cours */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-bold text-text mb-6">Vos cours</h2>
              {loading ? (
                <div className="space-y-4">
                  {[1,2,3].map(i => (
                    <div key={i} className="animate-pulse flex gap-4 p-4 border border-gray-100 rounded-lg">
                      <div className="w-16 h-12 bg-gray-200 rounded" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 w-3/4 bg-gray-200 rounded" />
                        <div className="h-3 w-1/2 bg-gray-100 rounded" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : courses.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <BookOpen className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm">Vous n'avez pas encore créé de cours.</p>
                  <button
                    onClick={() => navigate('/instructor/create-course')}
                    className="mt-3 text-primary text-sm font-medium hover:underline"
                  >
                    Créer votre premier cours →
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {courses.map((course, index) => (
                    <div
                      key={course.id}
                      className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg hover:border-primary/30 transition-colors"
                    >
                      {course.thumbnail ? (
                        <img
                          src={course.thumbnail}
                          alt={course.title}
                          className="w-16 h-12 object-cover rounded flex-shrink-0"
                        />
                      ) : (
                        <div className="w-16 h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0">
                          <BookOpen className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-text truncate">{course.title}</h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${statusClass[course.status] || 'bg-gray-100 text-gray-600'}`}>
                            {statusLabel[course.status] || course.status}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 flex-wrap">
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" />
                            {course.enrollments_count || 0} étudiants
                          </span>
                          {course.rating > 0 && (
                            <span className="flex items-center gap-1">
                              <Star className="w-4 h-4 text-yellow-400 fill-current" />
                              {course.rating}
                            </span>
                          )}
                          <select
                            value={DIFFICULTY_LABELS[course.difficulty] || course.difficulty || 'Débutant'}
                            onChange={e => changeLevel(course.id, e.target.value)}
                            onClick={e => e.stopPropagation()}
                            className="border border-gray-200 rounded px-1.5 py-0.5 text-xs text-gray-600 bg-white hover:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                            title="Changer le niveau"
                          >
                            <option>Débutant</option>
                            <option>Intermédiaire</option>
                            <option>Avancé</option>
                          </select>
                        </div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <div className="flex flex-col gap-0.5 mr-1">
                          <button
                            onClick={() => moveCourse(index, 'up')}
                            disabled={index === 0}
                            className="p-1 text-gray-300 hover:text-primary disabled:opacity-20 rounded transition-colors"
                            title="Monter"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => moveCourse(index, 'down')}
                            disabled={index === courses.length - 1}
                            className="p-1 text-gray-300 hover:text-primary disabled:opacity-20 rounded transition-colors"
                            title="Descendre"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </button>
                        </div>
                        <button
                          onClick={() => togglePublish(course.id, course.status)}
                          className={`p-2 rounded-lg transition-colors ${course.status === 'published' ? 'text-green-600 hover:text-yellow-600 hover:bg-yellow-50' : 'text-gray-400 hover:text-green-600 hover:bg-green-50'}`}
                          title={course.status === 'published' ? 'Dépublier' : 'Publier'}
                        >
                          {course.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Globe className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => navigate(`/course/${course.id}`)}
                          className="p-2 text-gray-400 hover:text-primary rounded-lg hover:bg-gray-50 transition-colors"
                          title="Voir"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/instructor/course/${course.id}/manage`)}
                          className="p-2 text-gray-400 hover:text-primary rounded-lg hover:bg-gray-50 transition-colors"
                          title="Gérer le contenu"
                        >
                          <Layout className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => navigate(`/instructor/course/${course.id}/edit`)}
                          className="p-2 text-gray-400 hover:text-primary rounded-lg hover:bg-gray-50 transition-colors"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Colonne droite */}
          <div className="space-y-6">
            {/* Activité récente */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-text mb-4">Activité récente</h3>
              {loading ? (
                <div className="space-y-3">
                  {[1,2,3].map(i => (
                    <div key={i} className="animate-pulse flex gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-gray-200 rounded-full mt-2 flex-shrink-0" />
                      <div className="flex-1 space-y-1">
                        <div className="h-3 bg-gray-200 rounded w-3/4" />
                        <div className="h-2 bg-gray-100 rounded w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : activity.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">Aucune activité récente</p>
              ) : (
                <div className="space-y-3">
                  {activity.map((a, i) => (
                    <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-2 h-2 bg-primary rounded-full mt-2 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-gray-700">{a.message}</p>
                        <p className="text-xs text-gray-400">{timeAgo(a.time)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions rapides */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-text mb-4">Actions rapides</h3>
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/instructor/create-course')}
                  className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="font-medium text-text text-sm">Créer un nouveau cours</div>
                  <div className="text-xs text-gray-500">Commencez un nouveau projet</div>
                </button>
                <button
                  onClick={() => navigate('/analytics')}
                  className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="font-medium text-text text-sm">Analyser les performances</div>
                  <div className="text-xs text-gray-500">Voir les statistiques détaillées</div>
                </button>
                <button
                  onClick={() => navigate('/admin')}
                  className="w-full text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="font-medium text-text text-sm">Administration</div>
                  <div className="text-xs text-gray-500">Gérer utilisateurs et contenus</div>
                </button>
              </div>
            </div>

            {/* Conseils */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-5">
              <h3 className="text-base font-semibold text-blue-900 mb-2"><Lightbulb className="w-4 h-4 inline mr-1 align-[-2px]" aria-hidden="true" />Conseils</h3>
              <ul className="text-sm text-blue-800 space-y-1.5">
                <li>• Publiez régulièrement du nouveau contenu</li>
                <li>• Répondez rapidement aux questions</li>
                <li>• Utilisez des exemples pratiques</li>
                <li>• Encouragez l'interaction</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Instructor;
